/**
 * ProductAccessPicker - the shared search + select-all/clear-all + checkbox
 * list body used to choose which products a partner can access.
 *
 * Extracted from PartnershipProductAccess so the same picker drives both the
 * post-acceptance "Manage Product Access" dialog and the invite dialog's
 * required product section - the two surfaces can't drift.
 */

import { useMemo } from 'react'
import { SearchInput } from '@/shared/components/ui/search-input'
import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import { Checkbox } from '@/shared/components/ui/checkbox'

export interface ProductAccessPickerProduct {
  uuid: string
  name: string
  status: string
  category: string | null
}

interface ProductAccessPickerProps {
  availableProducts: ProductAccessPickerProduct[]
  selected: Set<string>
  onToggle: (uuid: string) => void
  onSelectAll: () => void
  onClearAll: () => void
  search: string
  onSearch: (value: string) => void
  /** Message shown when the roster is genuinely empty (not while loading). */
  emptyMessage?: string
}

export function ProductAccessPicker({
  availableProducts,
  selected,
  onToggle,
  onSelectAll,
  onClearAll,
  search,
  onSearch,
  emptyMessage = 'No products found.',
}: ProductAccessPickerProps) {
  const filteredProducts = useMemo(() => {
    if (!search.trim()) return availableProducts
    const q = search.toLowerCase()
    return availableProducts.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.category && p.category.toLowerCase().includes(q))
    )
  }, [availableProducts, search])

  return (
    <div>
      {/* Search */}
      <div className="mb-3">
        <SearchInput
          value={search}
          onChange={onSearch}
          placeholder="Search products..."
          className="h-[36px] w-full rounded-[4px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] pr-3 text-[13px] text-[var(--ink)] focus:border-[var(--green)]"
        />
      </div>

      {/* Select/Clear all */}
      <div className="flex items-center gap-3 mb-2">
        <Button
          variant="link"
          type="button"
          onClick={onSelectAll}
          className="h-auto p-0 text-[10px] uppercase tracking-[0.1em] text-[var(--green)] hover:underline"
        >
          Select all
        </Button>
        <span className="text-[var(--rule-strong)]">&middot;</span>
        <Button
          variant="link"
          type="button"
          onClick={onClearAll}
          className="h-auto p-0 text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)] hover:underline"
        >
          Clear all
        </Button>
        <span className="ml-auto text-[11px] text-[var(--ink-3)]">
          {selected.size} of {availableProducts.length} selected
        </span>
      </div>

      {/* Product list */}
      <div className="max-h-[320px] overflow-y-auto rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)]">
        {filteredProducts.length === 0 ? (
          <p className="px-4 py-6 text-center text-[13px] text-[var(--ink-3)]">
            {emptyMessage}
          </p>
        ) : (
          filteredProducts.map((p) => (
            <Label
              key={p.uuid}
              className="flex cursor-pointer items-center gap-3 border-b-[0.5px] border-b-[var(--rule)] px-4 py-2.5 transition-colors duration-150 hover:bg-[var(--bg-subtle)]"
            >
              <Checkbox
                checked={selected.has(p.uuid)}
                onCheckedChange={() => onToggle(p.uuid)}
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] text-[var(--ink)]">
                  {p.name || 'Unnamed product'}
                </p>
                {p.category && (
                  <p className="truncate text-[10px] text-[var(--ink-3)]">
                    {p.category}
                  </p>
                )}
              </div>
              <span
                className={`shrink-0 rounded-[3px] px-1.5 py-0.5 text-[8px] uppercase tracking-[0.1em] ${
                  p.status === 'PUBLISHED'
                    ? 'bg-[var(--status-ok-bg)] text-[var(--status-ok)]'
                    : 'bg-[var(--bg-subtle)] text-[var(--ink-3)]'
                }`}
              >
                {p.status}
              </span>
            </Label>
          ))
        )}
      </div>
    </div>
  )
}
